import { useState, useCallback, useMemo } from 'react'
import { Plus, Upload, Box, Boxes, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { ObjectFormDialog } from '../components/ObjectFormDialog'
import { ObjectDeleteDialog } from '../components/ObjectDeleteDialog'
import { ObjectQRCodeDialog } from '../components/ObjectQRCodeDialog'
import { ObjectsByLocationDialog } from '../components/ObjectsByLocationDialog'
import { ObjectImportDialog } from '../components/ObjectImportDialog'
import { useObjects, useDeleteObject, useObjectsStatsCount } from '../hooks'
import { createObjectColumns } from '../columns'
import { objectsApi } from '../api'
import { AdminRoute } from '@/components/auth/AdminRoute'
import type { Object as ObjectEntity } from '@/types/entities'
import type { PaginatedResponse } from '@/lib/api/types'

export function ObjectListPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [selectedObject, setSelectedObject] = useState<ObjectEntity | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showQRCodeDialog, setShowQRCodeDialog] = useState(false)
    const [qrCodeObjectID, setQrCodeObjectID] = useState<string | null>(null)
    const [metadataDialog, setMetadataDialog] = useState<{ title: string; content: string | null } | null>(null)
    const [metadataLoading, setMetadataLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [objectServiceFilter, setObjectServiceFilter] = useState<'Object' | 'Service'>('Object')
    const [search, setSearch] = useState('')
    const [showLocationDialog, setShowLocationDialog] = useState(false)
    const [locationDialogType, setLocationDialogType] = useState<'objects' | 'services'>('objects')
    const [showImportDialog, setShowImportDialog] = useState(false)

    const { data: objectsResponse, isLoading } = useObjects({ 
        page, 
        limit, 
        objectService: objectServiceFilter,
        search: search || undefined // Only send search if not empty
    })
    const { data: statsCount } = useObjectsStatsCount()
    const deleteObject = useDeleteObject()

    // Extract objects array from paginated response
    const objects: ObjectEntity[] = useMemo(() => {
        if (!objectsResponse) return []
        // Backend returns { success: true, data: [...], page, perPage, total, ... }
        // Axios wraps it, so response.data contains the backend response
        const dataArray = objectsResponse.data
        if (!dataArray || !Array.isArray(dataArray)) {
            return []
        }
        return dataArray
    }, [objectsResponse])

    // Memoize callbacks to prevent re-creating on every render
    const handleEdit = useCallback((object: ObjectEntity) => {
        setSelectedObject(object)
        setShowEditDialog(true)
    }, [])

    const handleDelete = useCallback((object: ObjectEntity) => {
        setSelectedObject(object)
        setShowDeleteDialog(true)
    }, [])

    const handleView = useCallback((object: ObjectEntity) => {
        if (object.objectID) {
            // Encode the ObjectID to handle special characters
            navigate(`/objects/${encodeURIComponent(object.objectID)}`)
        }
    }, [navigate])

    const handleRowClick = useCallback((object: ObjectEntity) => {
        // Navigate to view page when row is clicked
        if (object.objectID) {
            // Encode the ObjectID to handle special characters
            navigate(`/objects/${encodeURIComponent(object.objectID)}`)
        }
    }, [navigate])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedObject?.objectID) {
            deleteObject.mutate(selectedObject.objectID, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedObject(null)
                },
            })
        }
    }, [selectedObject, deleteObject])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export objects to CSV')
    }, [])

    const handleShowQRCode = useCallback((objectID: string) => {
        setQrCodeObjectID(objectID)
        setShowQRCodeDialog(true)
    }, [])

    const handleShowMetadata = useCallback(async (objectID: string) => {
        setMetadataLoading(true)
        setMetadataDialog({ title: `${t('objects.metadata', 'Metadata')} - ${objectID}`, content: null })
        try {
            const res = await objectsApi.getVersions(objectID)
            const versions = res.data?.data ?? res.data
            const list = Array.isArray(versions) ? versions : []
            const metadata = list.length > 0 ? (list[0] as { versionMetadata?: string | null }).versionMetadata ?? null : null
            setMetadataDialog({ title: `${t('objects.metadata', 'Metadata')} - ${objectID}`, content: metadata || null })
        } catch {
            setMetadataDialog({ title: `${t('objects.metadata', 'Metadata')} - ${objectID}`, content: null })
        } finally {
            setMetadataLoading(false)
        }
    }, [t])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createObjectColumns(handleDelete, handleEdit, handleView, handleShowQRCode, handleShowMetadata, t, objectServiceFilter === 'Service'),
        [handleDelete, handleEdit, handleView, handleShowQRCode, handleShowMetadata, t, objectServiceFilter]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('objects.loadingObjects')}</div>
            </div>
        )
    }

    return (
        <AdminRoute>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('objects.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('objects.pageDescription')}
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                            <Upload className="mr-2 h-4 w-4" />
                            {t('objects.import.button')}
                        </Button>
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            {objectServiceFilter === 'Service' ? t('objects.createService') : t('objects.createObject')}
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="flex justify-center gap-4">
                        {/* Objects Card */}
                        <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                            <div className="flex items-center space-x-2">
                                <Box className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm font-medium">
                                        {t('objects.objects')}
                                    </p>
                                    <p className="text-2xl font-bold">
                                        {statsCount?.objects || 0}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute bottom-4 right-4"
                                onClick={() => {
                                    setLocationDialogType('objects')
                                    setShowLocationDialog(true)
                                }}
                            >
                                <MapPin className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Services Card */}
                        <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                            <div className="flex items-center space-x-2">
                                <Boxes className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm font-medium">
                                        {t('objects.services')}
                                    </p>
                                    <p className="text-2xl font-bold">
                                        {statsCount?.services || 0}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute bottom-4 right-4"
                                onClick={() => {
                                    setLocationDialogType('services')
                                    setShowLocationDialog(true)
                                }}
                            >
                                <MapPin className="h-4 w-4" />
                            </Button>
                        </div>
                </div>
            </div>

            {/* Table - Full Width - Extended */}
            <div 
                className="overflow-x-auto" 
                style={{ 
                    marginLeft: 'calc(var(--spacing-page) * -2)',
                    marginRight: 'calc(var(--spacing-page) * -2)',
                    paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                    paddingRight: 'calc(var(--spacing-page) * 0.05)',
                    width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                    maxWidth: 'none'
                }}
            >
                <DataTable                        columns={columns}
                        data={objects}
                        searchKey="objectDescription"
                        searchPlaceholder={t('objects.searchPlaceholder')}
                        searchValue={search}
                        onSearchChange={(value) => {
                            setSearch(value)
                            setPage(1) // Reset to first page when search changes
                        }}
                        onExport={handleExport}
                        exportButtonText={t('objects.export')}
                        enableRowSelection={false}
                        onRowClick={handleRowClick}
                        pagination={{
                            page,
                            limit,
                            total: objectsResponse?.total || 0,
                            onPageChange: setPage,
                            onPageSizeChange: setLimit,
                        }}
                        toolbarContent={
                            <div className="flex items-center gap-6 ml-8">
                                <span className="text-sm font-medium whitespace-nowrap">{t('objects.filterBy')}:</span>
                                <div className="flex rounded-lg border p-0.5 bg-muted/30 gap-2" role="group">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setObjectServiceFilter('Object')
                                            setPage(1)
                                        }}
                                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                            objectServiceFilter === 'Object' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        <Box className="h-4 w-4 text-blue-600" />
                                        {t('objects.objects')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setObjectServiceFilter('Service')
                                            setPage(1)
                                        }}
                                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                            objectServiceFilter === 'Service' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        <Boxes className="h-4 w-4 text-blue-600" />
                                        {t('objects.services')}
                                    </button>
                                </div>
                            </div>
                        }
                        pagination={{
                            page: objectsResponse?.page || page,
                            limit: objectsResponse?.perPage || limit,
                            total: objectsResponse?.total || 0,
                            onPageChange: setPage,
                            onPageSizeChange: (newLimit) => {
                                setLimit(newLimit)
                                setPage(1) // Reset to first page when changing page size
                            },
                        }}
                    />
            </div>

            {/* Dialogs */}
            <ObjectFormDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                object={null}
                objectService={objectServiceFilter}
            />

            <ObjectFormDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                object={selectedObject}
            />

            <ObjectDeleteDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                object={selectedObject}
                onConfirm={handleDeleteConfirm}
                isLoading={deleteObject.isPending}
            />

            <ObjectQRCodeDialog
                open={showQRCodeDialog}
                onOpenChange={setShowQRCodeDialog}
                objectID={qrCodeObjectID}
            />

            {metadataDialog && (
                <JsonViewDialog
                    open={!!metadataDialog}
                    onOpenChange={(open) => {
                        if (!open) setMetadataDialog(null)
                    }}
                    title={metadataLoading ? `${metadataDialog.title} (${t('common.loading', 'Loading...')})` : metadataDialog.title}
                    content={metadataDialog.content}
                />
            )}

            <ObjectsByLocationDialog
                open={showLocationDialog}
                onOpenChange={setShowLocationDialog}
                type={locationDialogType}
            />

            <ObjectImportDialog
                open={showImportDialog}
                onOpenChange={setShowImportDialog}
                objectService={objectServiceFilter}
            />
        </AdminRoute>
    )
}